import i18next, { i18n, Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, resolveUiLanguage, ENABLED_UI_LANGUAGES } from './languages';
import { readLanguagePreference, writeLanguagePreference } from './preference';
import { defaultNS, resources } from './resources';

// This describes the Nimos interface, never customer-facing document language.
function syncDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

// Overrides support isolated tests. Production initialization always uses defaults.
export function createI18nInstance(
  bundles: Resource = resources,
  languages: readonly string[] = ENABLED_UI_LANGUAGES,
  language: string = DEFAULT_LANGUAGE,
): i18n {
  const instance = i18next.createInstance();
  // Register once, before synchronous initialization. Tests can inject synthetic
  // enabled languages; the production instance always uses the enabled registry.
  instance.on('languageChanged', selected => {
    syncDocumentLanguage(languages.includes(selected) ? selected : DEFAULT_LANGUAGE);
  });
  void instance.use(initReactI18next).init({
    resources: bundles,
    lng: languages.includes(language) ? language : DEFAULT_LANGUAGE,
    supportedLngs: [...languages],
    fallbackLng: DEFAULT_LANGUAGE,
    load: 'currentOnly',
    defaultNS,
    ns: [defaultNS],
    initImmediate: false,
    returnNull: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false }, // React escapes rendered text.
    react: { useSuspense: false },
  });
  return instance;
}

let applicationInstance: i18n | undefined;

export function initializeI18n(): i18n {
  if (!applicationInstance) {
    applicationInstance = createI18nInstance(resources, ENABLED_UI_LANGUAGES, readLanguagePreference());
  }
  syncDocumentLanguage(resolveUiLanguage(applicationInstance.language));
  return applicationInstance;
}

// Application UI calls this API, never raw i18next.changeLanguage(): validation,
// immediate document-language synchronization, and safe persistence live here.
export async function setUiLanguage(value: unknown): Promise<void> {
  const language = resolveUiLanguage(value);
  await initializeI18n().changeLanguage(language);
  writeLanguagePreference(language);
}
